import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/useAuth";
import {
  dismissToast,
  forgetJob,
  isTerminal,
  trackedIds,
  toastFor,
  upsertToast,
  useJobToasts,
  type JobToast,
  type RulesBulkJob,
} from "@/lib/rules-bulk-jobs";

const POLL_MS = 2500;
const AUTO_DISMISS_MS = 6000;

/** Данные, которые меняет Apply / Stop rules: графа правил и фильтр Rules в
 *  трёх таблицах Ads Manager, поисковые запросы, список правил и привязки. */
const AFFECTED_KEYS = [
  "stats-campaigns",
  "stats-ad-groups",
  "stats-keywords",
  "search-terms",
  "rules-v2",
  "rule-applications",
];

/**
 * Тостеры фоновых Apply / Stop rules. Смонтирован один раз над роутером:
 * итог приходит, на какой бы странице человек ни был. Опрашивает
 * незаконченные задания раз в 2,5 с; после перезагрузки подхватывает их
 * из localStorage и из списка активных на сервере.
 */
export function RulesBulkJobsToaster() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toasts = useJobToasts();
  const live = useRef(new Set<number>());

  useEffect(() => {
    if (!user) return;
    let stopped = false;

    const apply = (job: RulesBulkJob) => {
      upsertToast(toastFor(job));
      if (isTerminal(job.status)) {
        live.current.delete(job.job_id);
        forgetJob(job.job_id);
        for (const key of AFFECTED_KEYS) void qc.invalidateQueries({ queryKey: [key] });
      } else {
        live.current.add(job.job_id);
      }
    };

    const poll = async () => {
      const ids = new Set<number>([...live.current, ...trackedIds()]);
      for (const id of ids) {
        try {
          const { data } = await api.get<RulesBulkJob>(`/api/rules/bulk-apply-jobs/${id}`);
          if (!stopped) apply(data);
        } catch (err) {
          const st = (err as { response?: { status?: number } })?.response?.status;
          if (st === 404) {
            // Задание другой организации или пользователя (сменили org):
            // здесь его не видно — перестаём о нём спрашивать.
            live.current.delete(id);
            forgetJob(id);
            dismissToast(`job-${id}`);
          }
        }
      }
    };

    // Вкладка открылась (или перезагрузилась): незаконченные — с сервера.
    void (async () => {
      try {
        const { data } = await api.get<{ jobs: RulesBulkJob[] }>("/api/rules/bulk-apply-jobs");
        if (!stopped) data.jobs.forEach(apply);
      } catch {
        /* опрос ниже всё равно пройдёт по локальному списку */
      }
      await poll();
    })();

    const t = setInterval(() => void poll(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [user, qc]);

  // Задания, заведённые модалкой, попадают в опрос сразу.
  useEffect(() => {
    for (const t of toasts) {
      if (t.jobId != null && t.kind === "info") live.current.add(t.jobId);
    }
  }, [toasts]);

  if (!user || toasts.length === 0) return null;
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        maxWidth: "min(560px, calc(100vw - 32px))",
      }}
    >
      {toasts.map((t) => (
        <JobToastView key={t.key} toast={t} />
      ))}
    </div>,
    document.body,
  );
}

const PALETTE: Record<JobToast["kind"], { bg: string; shadow: string; glyph: string }> = {
  info: { bg: "rgba(30,41,59,0.96)", shadow: "0 8px 24px rgba(15,23,42,0.3)", glyph: "…" },
  success: { bg: "rgba(34,197,94,0.96)", shadow: "0 8px 24px rgba(34,197,94,0.35)", glyph: "✓" },
  warning: { bg: "rgba(217,119,6,0.97)", shadow: "0 8px 24px rgba(217,119,6,0.35)", glyph: "!" },
  error: { bg: "rgba(239,68,68,0.96)", shadow: "0 8px 24px rgba(239,68,68,0.35)", glyph: "✕" },
};

function JobToastView({ toast }: { toast: JobToast }) {
  useEffect(() => {
    if (toast.sticky) return;
    const t = setTimeout(() => dismissToast(toast.key), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [toast.key, toast.sticky, toast.kind]);

  const p = PALETTE[toast.kind];
  const closable = toast.kind === "warning" || toast.kind === "error";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: toast.details.length ? "10px 14px 12px 16px" : "10px 14px 10px 16px",
        borderRadius: toast.details.length ? 14 : 999,
        background: p.bg,
        color: "#fff",
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: 0.1,
        boxShadow: `${p.shadow}, 0 1px 3px rgba(0,0,0,0.2)`,
        animation: "toast-in 0.24s ease-out",
        pointerEvents: closable ? "auto" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
          {p.glyph}
        </span>
        <span style={{ flex: 1 }}>{toast.text}</span>
        {closable && (
          <button
            type="button"
            aria-label="Close"
            onClick={() => dismissToast(toast.key)}
            style={{
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.32)",
              color: "#fff",
              fontSize: 12,
              lineHeight: 1,
              padding: "4px 8px",
              borderRadius: 999,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        )}
      </div>
      {toast.details.length > 0 && (
        <ul style={{ margin: "8px 0 0 24px", padding: 0, fontSize: 12.5, fontWeight: 400, lineHeight: 1.45 }}>
          {toast.details.map((d, i) => (
            <li key={i}>
              {d.rule_name ? <b>{d.rule_name}</b> : null}
              {d.rule_name ? ": " : ""}
              {d.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
